import { useCallback, useEffect, useState } from 'react';
import { useQueryParamHelpers } from './useQueryParamHelpers';

type SetQueryParamFunction<T> = (value: T | ((prevValue: T) => T)) => void;

export function useQueryParam<T extends string>(
  key: string,
  defaultValue: T = '' as T,
): [T, SetQueryParamFunction<T>] {
  const { getQueryParam, setQueryParam } = useQueryParamHelpers();
  const [value, setValue] = useState<T>(() => {
    const paramValue = getQueryParam(key);
    return (paramValue || defaultValue) as T;
  });

  useEffect(() => {
    const paramValue = getQueryParam(key);
    if (paramValue !== value) {
      setValue(paramValue as T);
    }
  }, [key, getQueryParam]);

  const setParamValue = useCallback(
    (newValue: T | ((prevValue: T) => T)) => {
      const actualValue =
        typeof newValue === 'function'
          ? (newValue as (prevValue: T) => T)(value)
          : newValue;

      setValue(actualValue);
      setQueryParam(key, actualValue);
    },
    [key, setQueryParam, value],
  );

  return [value, setParamValue];
}
